import type { components } from 'admination-react/api-schema'
import { ApiError, sendForm, sendJson } from '@/core/api/http'

type Schemas = components['schemas']

export type ZuweisungslisteUebersprungen = Schemas['UebersprungenEintrag']

export type ZuweisungslisteMassnahme = Schemas['ZuweisungslisteMassnahme']

export type ZuweisungslisteResult = Schemas['ZuweisungslisteUploadResult']

export type ZuweisungslisteFehlerZeile = {
  zeile: number | null
  spalte: string | null
  feld: string | null
  grund: string
}

export type ZuweisungslisteErrorBody = {
  meldung: string
  fehler: ZuweisungslisteFehlerZeile[]
}

export function uploadZuweisungsliste(file: File): Promise<ZuweisungslisteResult> {
  const form = new FormData()
  form.append('datei', file, file.name)
  return sendForm<ZuweisungslisteResult>('/zuweisungsliste', form)
}

export type TeilnehmendePerson = Schemas['TeilnehmendePersonAnlegen']

export function anlegenTeilnehmendePerson(person: TeilnehmendePerson): Promise<ZuweisungslisteResult> {
  return sendJson<ZuweisungslisteResult>('/zuweisungsliste/person', 'POST', person)
}

export function isZuweisungslisteResult(body: unknown): body is ZuweisungslisteResult {
  if (!body || typeof body !== 'object') return false
  const { angelegt, uebersprungen, massnahme, massnahmenummer_unbekannt } = body as {
    angelegt?: unknown
    uebersprungen?: unknown
    massnahme?: unknown
    massnahmenummer_unbekannt?: unknown
  }
  if (typeof angelegt !== 'number' || !Array.isArray(uebersprungen)) return false
  if (massnahme !== null && !isZuweisungslisteMassnahme(massnahme)) return false
  if (massnahmenummer_unbekannt !== null && typeof massnahmenummer_unbekannt !== 'string') return false
  return uebersprungen.every(isZuweisungslisteUebersprungenRow)
}

function isZuweisungslisteMassnahme(item: unknown): item is ZuweisungslisteMassnahme {
  if (!item || typeof item !== 'object') return false
  const { id, nummer, bezeichnung } = item as { id?: unknown; nummer?: unknown; bezeichnung?: unknown }
  return typeof id === 'string' && typeof nummer === 'string' && typeof bezeichnung === 'string'
}

function isZuweisungslisteUebersprungenRow(item: unknown): item is ZuweisungslisteUebersprungen {
  if (!item || typeof item !== 'object') return false
  const { zeile, kundennummer, nachname, vorname } = item as {
    zeile?: unknown
    kundennummer?: unknown
    nachname?: unknown
    vorname?: unknown
  }
  return (
    typeof zeile === 'number' &&
    typeof kundennummer === 'string' &&
    typeof nachname === 'string' &&
    typeof vorname === 'string'
  )
}

function isZuweisungslisteFehlerZeile(item: unknown): item is ZuweisungslisteFehlerZeile {
  if (!item || typeof item !== 'object') return false
  const { zeile, grund } = item as { zeile?: unknown; grund?: unknown }
  return (zeile === null || typeof zeile === 'number') && typeof grund === 'string'
}

export function zuweisungslisteErrorBody(error: unknown): ZuweisungslisteErrorBody | null {
  if (!(error instanceof ApiError)) return null
  const body = error.detail
  if (!body || typeof body !== 'object' || !('detail' in body)) return null
  const inner = (body as { detail: unknown }).detail
  if (!inner || typeof inner !== 'object') return null
  const { meldung, fehler } = inner as { meldung?: unknown; fehler?: unknown }
  if (typeof meldung !== 'string' || !Array.isArray(fehler)) return null
  if (!fehler.every(isZuweisungslisteFehlerZeile)) return null
  return { meldung, fehler }
}
